import { PLAYER_DAMAGE } from '../game/CombatBalance.js'

export const BLOCK_TYPES = {
  AIR: 0,
  DIRT: 1,
  GRASS: 2,
  STONE: 3,
  WOOD: 4,
  LEAVES: 5,
  SAND: 6,
  WATER: 7,
  COBBLESTONE: 8,
  PLANKS: 9,
  IRON_ORE: 10,
  DIAMOND_ORE: 11,
  OBSIDIAN: 12,
  MYSTIC_STONE: 13,
  LAVA: 14,
  GRAVEL: 15,
  TORCH: 16,
  BRICK: 17,
  MOSSY_COBBLESTONE: 18,
  BONE_BLOCK: 19,
  DARK_STONE: 20,
  DARK_BRICK: 21,
  ASH: 22,
  TREE: 23,
  GRAVESTONE: 24,
  PATH: 25,
  WALL: 26,
  KEY: 27,
  LOCKED_DOOR: 28,
  DOOR: 29,
  WOOD_FLOOR: 30,
  HOME_WALL: 31
}

// Colors as CSS hex strings for 2D rendering
export const BLOCK_COLORS = {
  [BLOCK_TYPES.AIR]: 'transparent',
  [BLOCK_TYPES.DIRT]: '#8B4513',
  [BLOCK_TYPES.GRASS]: '#3a8a3a',
  [BLOCK_TYPES.STONE]: '#808080',
  [BLOCK_TYPES.WOOD]: '#654321',
  [BLOCK_TYPES.LEAVES]: '#228B22',
  [BLOCK_TYPES.SAND]: '#C2B280',
  [BLOCK_TYPES.WATER]: '#4169E1',
  [BLOCK_TYPES.COBBLESTONE]: '#696969',
  [BLOCK_TYPES.PLANKS]: '#DEB887',
  [BLOCK_TYPES.IRON_ORE]: '#A0A0A0',
  [BLOCK_TYPES.DIAMOND_ORE]: '#00CED1',
  [BLOCK_TYPES.OBSIDIAN]: '#1a1a2e',
  [BLOCK_TYPES.MYSTIC_STONE]: '#4a0080',
  [BLOCK_TYPES.LAVA]: '#FF4500',
  [BLOCK_TYPES.GRAVEL]: '#5a5a5a',
  [BLOCK_TYPES.TORCH]: '#FFD700',
  [BLOCK_TYPES.BRICK]: '#c45c4a',
  [BLOCK_TYPES.MOSSY_COBBLESTONE]: '#4a6a4a',
  [BLOCK_TYPES.BONE_BLOCK]: '#F5F5DC',
  [BLOCK_TYPES.DARK_STONE]: '#2a2a3a',
  [BLOCK_TYPES.DARK_BRICK]: '#4a0a0a',
  [BLOCK_TYPES.ASH]: '#3a3a3a',
  [BLOCK_TYPES.TREE]: '#2d5a2d',
  [BLOCK_TYPES.GRAVESTONE]: '#666666',
  [BLOCK_TYPES.PATH]: '#aa9977',
  [BLOCK_TYPES.WALL]: '#667788',
  [BLOCK_TYPES.KEY]: '#f6d44a',
  [BLOCK_TYPES.LOCKED_DOOR]: '#ffcc33',
  [BLOCK_TYPES.DOOR]: '#c89a5a',
  [BLOCK_TYPES.WOOD_FLOOR]: '#b97942',
  [BLOCK_TYPES.HOME_WALL]: '#704126'
}

// raised = block visually sits above ground and can potentially be mined (if player-placed)
// mineable = player can mine this block type (only applies to player-placed raised blocks)
export const BLOCK_PROPERTIES = {
  [BLOCK_TYPES.AIR]: { transparent: true, solid: false, raised: false, mineable: false },
  [BLOCK_TYPES.DIRT]: { transparent: false, solid: false, raised: false, mineable: false },
  [BLOCK_TYPES.GRASS]: { transparent: false, solid: false, raised: false, mineable: false },
  [BLOCK_TYPES.STONE]: { transparent: false, solid: false, raised: false, mineable: false },
  [BLOCK_TYPES.WOOD]: { transparent: false, solid: true, raised: true, mineable: true },
  [BLOCK_TYPES.LEAVES]: { transparent: true, solid: false, raised: false, mineable: false },
  [BLOCK_TYPES.SAND]: { transparent: false, solid: false, raised: false, mineable: false },
  [BLOCK_TYPES.WATER]: { transparent: true, solid: false, raised: false, mineable: false },
  [BLOCK_TYPES.COBBLESTONE]: { transparent: false, solid: false, raised: false, mineable: false },
  [BLOCK_TYPES.PLANKS]: { transparent: false, solid: true, raised: true, mineable: true },
  [BLOCK_TYPES.IRON_ORE]: { transparent: false, solid: false, raised: false, mineable: false },
  [BLOCK_TYPES.DIAMOND_ORE]: { transparent: false, solid: false, raised: false, mineable: false },
  [BLOCK_TYPES.OBSIDIAN]: { transparent: false, solid: false, raised: false, mineable: false },
  [BLOCK_TYPES.MYSTIC_STONE]: { transparent: false, solid: false, raised: false, mineable: false },
  [BLOCK_TYPES.LAVA]: { transparent: true, solid: false, raised: false, mineable: false, damage: PLAYER_DAMAGE.LIGHT },
  [BLOCK_TYPES.GRAVEL]: { transparent: false, solid: false, raised: false, mineable: false },
  [BLOCK_TYPES.TORCH]: { transparent: true, solid: false, raised: false, mineable: false, light: 15 },
  [BLOCK_TYPES.BRICK]: { transparent: false, solid: false, raised: false, mineable: false },
  [BLOCK_TYPES.MOSSY_COBBLESTONE]: { transparent: false, solid: false, raised: false, mineable: false },
  [BLOCK_TYPES.BONE_BLOCK]: { transparent: false, solid: false, raised: false, mineable: false },
  [BLOCK_TYPES.DARK_STONE]: { transparent: false, solid: false, raised: false, mineable: false },
  [BLOCK_TYPES.DARK_BRICK]: { transparent: false, solid: false, raised: false, mineable: false },
  [BLOCK_TYPES.ASH]: { transparent: false, solid: false, raised: false, mineable: false },
  [BLOCK_TYPES.TREE]: { transparent: false, solid: true, raised: true, mineable: false },
  [BLOCK_TYPES.GRAVESTONE]: { transparent: false, solid: true, raised: true, mineable: false },
  [BLOCK_TYPES.PATH]: { transparent: false, solid: false, raised: false, mineable: false },
  [BLOCK_TYPES.WALL]: { transparent: false, solid: true, raised: true, mineable: false },
  [BLOCK_TYPES.KEY]: { transparent: true, solid: false, raised: false, mineable: false },
  [BLOCK_TYPES.LOCKED_DOOR]: { transparent: false, solid: true, raised: true, mineable: false },
  [BLOCK_TYPES.DOOR]: { transparent: false, solid: false, raised: false, mineable: false },
  [BLOCK_TYPES.WOOD_FLOOR]: { transparent: false, solid: false, raised: false, mineable: false },
  [BLOCK_TYPES.HOME_WALL]: { transparent: false, solid: true, raised: true, mineable: false }
}

export function getBlockColor(type) {
  return BLOCK_COLORS[type] || '#FF00FF'
}

export function isBlockSolid(type) {
  if (type === BLOCK_TYPES.AIR || type === 0 || type === undefined) return false
  const props = BLOCK_PROPERTIES[type]
  return props ? props.solid : true
}

export function isBlockTransparent(type) {
  const props = BLOCK_PROPERTIES[type]
  return props ? props.transparent : false
}

export function isBlockRaised(type) {
  const props = BLOCK_PROPERTIES[type]
  return props ? props.raised : false
}

export function isBlockMineable(type) {
  const props = BLOCK_PROPERTIES[type]
  return props ? props.mineable : false
}
